import {
  SYNTH_MAX_TRACKS,
  createDefaultSynthResource,
  createDefaultTrack,
  normalizeSynthResource,
} from './synthModel.js'
import { applyInstrumentPreset, gmProgramToInstrument } from './synthInstruments.js'

const SYNTH_PPQ = 480

/** Import a Standard MIDI File into the shared synth domain model. */
export function importMidiFile(arrayBuffer) {
  const reader = new MidiReader(arrayBuffer)
  if (reader.readAscii(4) !== 'MThd') throw new Error('Invalid MIDI file: missing MThd header.')
  const headerLength = reader.readUint32()
  if (headerLength < 6) throw new Error('Invalid MIDI file: invalid MThd chunk length.')
  const headerEnd = reader.position + headerLength
  reader.ensure(headerLength)
  const format = reader.readUint16()
  const trackCount = reader.readUint16()
  const division = reader.readUint16()
  if (format !== 0 && format !== 1) throw new Error(`Unsupported MIDI format: ${format}.`)
  if (division & 0x8000) throw new Error('Unsupported MIDI timing: SMPTE time division is not supported.')
  if (division === 0) throw new Error('Invalid MIDI file: PPQ must be greater than zero.')
  reader.position = headerEnd

  const parsedTracks = []
  const tempoEvents = []
  for (let index = 0; index < trackCount; index++) {
    if (reader.readAscii(4) !== 'MTrk') throw new Error(`Invalid MIDI file: missing MTrk chunk ${index + 1}.`)
    const chunkLength = reader.readUint32()
    const chunkEnd = reader.position + chunkLength
    reader.ensure(chunkLength)
    parsedTracks.push(parseTrack(reader, chunkEnd, index, tempoEvents))
    reader.position = chunkEnd
  }

  const warnings = []
  const sortedTempos = tempoEvents.sort((a, b) => a.tick - b.tick || a.order - b.order)
  const initialTempo = sortedTempos[0]?.microsecondsPerQuarter ?? 500000
  if (sortedTempos.length > 1) {
    warnings.push(warning('tempo-changes', `MIDI contains tempo changes; only the initial ${formatBpm(initialTempo)} BPM tempo was imported.`))
  }

  const sources = buildNoteSources(parsedTracks, format, division)
  const selectedSources = sources.slice(0, SYNTH_MAX_TRACKS)
  if (sources.length > SYNTH_MAX_TRACKS) {
    warnings.push(warning('track-limit', `MIDI contains ${sources.length} instrument tracks; only ${SYNTH_MAX_TRACKS} can be imported.`))
  }

  const staged = createDefaultSynthResource()
  staged.tempo.bpm = 60000000 / initialTempo
  for (let index = 0; index < SYNTH_MAX_TRACKS; index++) {
    const source = selectedSources[index]
    if (!source) continue
    let track = createDefaultTrack(index)
    track.name = source.name
    track = applyInstrumentPreset(track, gmProgramToInstrument(source.program))
    track.volume = source.volume ?? 1
    track.sequence = notesToTimeline(source.notes)
    staged.tracks[index] = track
  }

  return { synth: normalizeSynthResource(staged), warnings }
}

function parseTrack(reader, chunkEnd, trackIndex, tempoEvents) {
  const notes = []
  const activeNotes = new Map()
  const channelPrograms = new Map()
  const channelVolumes = new Map()
  let tick = 0
  let runningStatus = null
  let name = `MIDI Track ${trackIndex + 1}`

  while (reader.position < chunkEnd) {
    tick += reader.readVlq(chunkEnd)
    reader.ensureWithin(1, chunkEnd)
    let status = reader.peekUint8()
    if (status & 0x80) {
      status = reader.readUint8()
      runningStatus = status < 0xf0 ? status : null
    } else {
      if (runningStatus == null) throw new Error('Invalid MIDI file: data byte encountered without running status.')
      status = runningStatus
    }

    if (status === 0xff) {
      reader.ensureWithin(1, chunkEnd)
      const type = reader.readUint8()
      const length = reader.readVlq(chunkEnd)
      reader.ensureWithin(length, chunkEnd)
      if (type === 0x03) name = decodeText(reader.readBytes(length)) || name
      else if (type === 0x51 && length === 3) {
        const microsecondsPerQuarter = reader.readUint24()
        if (microsecondsPerQuarter > 0) tempoEvents.push({ tick, microsecondsPerQuarter, order: tempoEvents.length })
      } else reader.position += length
      if (type === 0x2f) break
      continue
    }

    if (status === 0xf0 || status === 0xf7) {
      const length = reader.readVlq(chunkEnd)
      reader.ensureWithin(length, chunkEnd)
      reader.position += length
      continue
    }

    const command = status & 0xf0
    const channel = status & 0x0f
    if (![0x80, 0x90, 0xa0, 0xb0, 0xc0, 0xd0, 0xe0].includes(command)) {
      throw new Error(`Invalid MIDI file: unsupported status 0x${status.toString(16)}.`)
    }
    const dataLength = command === 0xc0 || command === 0xd0 ? 1 : 2
    const data1 = reader.readDataByte(chunkEnd)
    const data2 = dataLength === 2 ? reader.readDataByte(chunkEnd) : 0
    if (command === 0xc0) channelPrograms.set(channel, data1)
    else if (command === 0xb0 && data1 === 7) channelVolumes.set(channel, data2 / 127)
    else if (command === 0x90 && data2 > 0) startNote(activeNotes, channel, data1, tick, data2, channelPrograms.get(channel), channelVolumes.get(channel))
    else if (command === 0x80 || command === 0x90) endNote(activeNotes, notes, channel, data1, tick)
  }

  return { index: trackIndex, name, notes }
}

function startNote(activeNotes, channel, note, tick, velocity, program, volume) {
  const key = `${channel}:${note}`
  const queue = activeNotes.get(key) ?? []
  queue.push({ channel, note, startTick: tick, velocity, program, volume })
  activeNotes.set(key, queue)
}

function endNote(activeNotes, notes, channel, note, tick) {
  const key = `${channel}:${note}`
  const queue = activeNotes.get(key)
  const active = queue?.shift()
  if (!active || tick <= active.startTick) return
  notes.push({ ...active, endTick: tick })
  if (queue.length === 0) activeNotes.delete(key)
}

function buildNoteSources(tracks, format, midiPpq) {
  if (format === 0) return tracks.flatMap(track => groupTrackNotesByChannel(track, midiPpq))
  return tracks.map(track => ({
    name: track.name,
    notes: normalizeNotes(track.notes, midiPpq),
    program: track.notes.find(note => note.program != null)?.program,
    volume: track.notes.find(note => note.volume != null)?.volume,
  })).filter(source => source.notes.length > 0)
}

function groupTrackNotesByChannel(track, midiPpq) {
  const channels = new Map()
  for (const note of track.notes) {
    if (!channels.has(note.channel)) channels.set(note.channel, [])
    channels.get(note.channel).push(note)
  }
  return [...channels.entries()].map(([channel, notes]) => ({
    name: channels.size > 1 ? `${track.name} ch ${channel + 1}` : track.name,
    notes: normalizeNotes(notes, midiPpq),
    program: notes.find(note => note.program != null)?.program,
    volume: notes.find(note => note.volume != null)?.volume,
  })).filter(source => source.notes.length > 0)
}

function normalizeNotes(notes, midiPpq) {
  return notes.map(note => ({
    note: note.note,
    velocity: note.velocity,
    startTick: scaleTick(note.startTick, midiPpq),
    endTick: scaleTick(note.endTick, midiPpq),
  })).filter(note => note.endTick > note.startTick)
}

function notesToTimeline(notes) {
  return notes.map(note => ({
    note: note.note,
    startTick: note.startTick,
    durationTicks: note.endTick - note.startTick,
  })).filter(event => event.durationTicks > 0)
    .sort((a, b) => a.startTick - b.startTick || a.note - b.note || a.durationTicks - b.durationTicks)
}

function scaleTick(tick, midiPpq) {
  return Math.round(tick * SYNTH_PPQ / midiPpq)
}

function formatBpm(microsecondsPerQuarter) {
  return Number((60000000 / microsecondsPerQuarter).toFixed(2))
}

function warning(code, message) {
  return { code, message }
}

function decodeText(bytes) {
  return new TextDecoder().decode(bytes).replace(/\0/g, '').trim()
}

class MidiReader {
  constructor(arrayBuffer) {
    if (!(arrayBuffer instanceof ArrayBuffer)) throw new Error('Invalid MIDI file: expected binary data.')
    this.bytes = new Uint8Array(arrayBuffer)
    this.position = 0
  }

  ensure(length) {
    if (length < 0 || this.position + length > this.bytes.length) throw new Error('Invalid MIDI file: premature end of file.')
  }

  ensureWithin(length, end) {
    this.ensure(length)
    if (this.position + length > end) throw new Error('Invalid MIDI file: event exceeds its track chunk length.')
  }

  peekUint8() {
    this.ensure(1)
    return this.bytes[this.position]
  }

  readUint8() {
    this.ensure(1)
    return this.bytes[this.position++]
  }

  readDataByte(end) {
    this.ensureWithin(1, end)
    const value = this.readUint8()
    if (value & 0x80) throw new Error('Invalid MIDI file: expected a MIDI data byte.')
    return value
  }

  readUint16() {
    return (this.readUint8() << 8) | this.readUint8()
  }

  readUint24() {
    return (this.readUint8() << 16) | (this.readUint8() << 8) | this.readUint8()
  }

  readUint32() {
    return ((this.readUint8() * 0x1000000) + (this.readUint8() << 16) + (this.readUint8() << 8) + this.readUint8()) >>> 0
  }

  readAscii(length) {
    return String.fromCharCode(...this.readBytes(length))
  }

  readBytes(length) {
    this.ensure(length)
    const value = this.bytes.slice(this.position, this.position + length)
    this.position += length
    return value
  }

  readVlq(end) {
    let value = 0
    for (let index = 0; index < 4; index++) {
      this.ensureWithin(1, end)
      const byte = this.readUint8()
      value = (value * 128) + (byte & 0x7f)
      if (!(byte & 0x80)) return value
    }
    throw new Error('Invalid MIDI file: invalid variable-length quantity.')
  }
}
